import type { Router } from 'vue-router'

import pinia from '@/stores/pinia'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useStudioStore } from '@/modules/studio/stores/studio.store'
import { useAppContextStore } from '@/stores/app-context.store'
import { useSubscriptionStore } from '@/stores/subscription.store'

export const setupGuards = (router: Router) => {
  router.beforeEach(async (to) => {
    const authStore = useAuthStore(pinia)
    const studioStore = useStudioStore(pinia)
    const contextStore = useAppContextStore(pinia)
    const subscriptionStore = useSubscriptionStore(pinia)

    // ── Cargar perfil si hay token pero no hay user ─────────────────────────
    // Es seguro hacer await aquí porque /auth/me:
    //   - Con token válido → devuelve 200, no causa redirección secundaria
    //   - Con token inválido → 401 → interceptor llama logout() que pone
    //     isAuthenticated=false, y el guard a continuación envía a /login
    //     sin crear un loop (guestOnly solo redirige si isAuthenticated=TRUE)
    if (authStore.token && !authStore.user) {
      try {
        await authStore.loadProfile()
      } catch {
        // El interceptor ya hizo logout + router.replace('/login')
        // Retornamos false para cancelar la navegación actual
        return false
      }
    }

    // ── Rutas solo para guests ──────────────────────────────────────────────
    if (to.meta.guestOnly && authStore.isAuthenticated) {
      return { path: authStore.isSuperAdmin ? '/admin' : '/' }
    }

    // ── Rutas que requieren auth ────────────────────────────────────────────
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      return { path: '/login', query: { redirect: to.fullPath } }
    }

    // ── Super admin: acceso total, sin restricción de roles ni onboarding ───
    if (authStore.isSuperAdmin) {
      // Si intenta ir a '/' redirigir al panel de admin
      if (to.path === '/') return { path: '/admin' }
      return true
    }

    // ── Verificar roles ─────────────────────────────────────────────────────
    if (to.meta.roles && authStore.role && !(to.meta.roles as string[]).includes(authStore.role)) {
      return { path: '/login' }
    }

    // ── Selección de studio para multi-studio ───────────────────────────────
    if (
      to.meta.requiresAuth &&
      studioStore.hasMultipleStudios &&
      !studioStore.currentStudioId &&
      to.path !== '/studio/select' &&
      to.path !== '/onboarding'
    ) {
      return { path: '/studio/select' }
    }

    // ── Onboarding: solo verificar con contexto ya cargado ──────────────────
    if (
      authStore.isAuthenticated &&
      to.meta.requiresAuth &&
      !to.meta.skipOnboardingCheck &&
      to.path !== '/onboarding' &&
      contextStore.loaded &&
      !contextStore.loadError
    ) {
      if (contextStore.redirectTo === '/onboarding' || !contextStore.onboardingComplete) {
        return { path: '/onboarding' }
      }
    }

    // ── Suscripción: bloquear rutas que la requieren ─────────────────────────
    // Si el contexto ya está cargado (app/context incluye subscription), usarlo
    // directamente para evitar un fetch adicional. Si no está cargado aún,
    // hacemos fallback al subscriptionStore como antes.
    // En ambos casos, fallamos abierto si hay error de carga.
    if (to.meta.requiresSubscription && authStore.isAuthenticated) {
      if (contextStore.loaded && !contextStore.loadError) {
        if (!contextStore.canUseWhatsapp) {
          return { path: '/settings/billing' }
        }
      } else {
        if (!subscriptionStore.hasLoaded) {
          await subscriptionStore.fetchSubscription()
        }
        if (subscriptionStore.hasLoaded && !subscriptionStore.loadError && !subscriptionStore.canUseWhatsapp) {
          return { path: '/settings/billing' }
        }
      }
    }

    // ── Redirigir fuera de /onboarding si ya está completo ──────────────────
    if (
      to.path === '/onboarding' &&
      authStore.isAuthenticated &&
      contextStore.loaded &&
      !contextStore.loadError &&
      contextStore.onboardingComplete
    ) {
      const target = contextStore.redirectTo
      return { path: target && target !== '/onboarding' ? target : '/' }
    }

    return true
  })
}
